/**
 * Bridges auth → consent (RQ78378 / RQ78379).
 *
 * ConsentProvider sits above AuthProvider (so the banner works logged-out), so
 * it cannot observe auth directly. This tiny component lives INSIDE AuthProvider,
 * consumes both contexts, and on sign-in writes a NEW account-link audit row
 * (fresh consent_event_id, source_action='account_link') that associates the
 * prior device-keyed consent decision with the now-known account. The request is
 * authenticated, so the backend keys the row by the account id from the token.
 *
 * Renders nothing.
 */
import { useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { postConsent } from '../api/consent'
import { createNadDebug } from '../utils/debugLog'
import { useConsent } from './useConsent'
import { generateUuid, getLinkedAccount, setLinkedAccount } from './consentStorage'

const log = createNadDebug('dw:cookie-consent')

export function ConsentAuthBridge() {
  const { user } = useAuth()
  const { consent, jurisdiction, policyVersion, gpc } = useConsent()
  // Seed from persisted state so a plain reload of an already-linked session does
  // NOT post a duplicate account_link row. The marker is keyed by
  // account id + the linked consent decision (its eventId), so a NEW decision
  // (e.g. forced re-consent after a policy bump) or an account switch re-links,
  // while a plain reload of the same account+decision does not.
  const linkedRef = useRef<string | null>(getLinkedAccount())

  useEffect(() => {
    const userId = user?.id ?? null
    if (!userId) return
    if (!consent || !jurisdiction || !policyVersion) return
    const linkKey = `${userId}:${consent.eventId}`
    if (linkedRef.current === linkKey) return

    linkedRef.current = linkKey
    log({ event: 'account_link', userId })
    void postConsent({
      // Fresh id — do NOT reuse the device decision's eventId, or the idempotent
      // server insert would drop this account-keyed row.
      consent_event_id: generateUuid(),
      device_id: consent.did,
      categories: consent.cats,
      jurisdiction,
      policy_version: policyVersion,
      source_action: 'account_link',
      gpc,
    })
      .then(() => {
        // Persist so subsequent reloads skip re-linking the same decision.
        setLinkedAccount(linkKey)
      })
      .catch((err) => {
        // Best-effort; allow a retry on a later mount/auth change.
        linkedRef.current = null
        log({ event: 'account_link_failed', error: String(err) })
      })
  }, [user?.id, consent, jurisdiction, policyVersion, gpc])

  return null
}
